import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { HostProductCodeList } from "src/app/features/HostProductCode/types/HostProductCodeList";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import { displayDateTimeValue } from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const HostProductCodesTable: FC<ResourceTableProps<HostProductCodeList>> = ({
    onStateChanged,
    onRowSelectionChange,
    datasource,
    resource,
}) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<HostProductCodeList>): ExtendedColumnsType<HostProductCodeList>[] => {
            return [
                {
                    title: t("genericTable.column.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("hostProductCodeList.acquirer"),
                    key: "acquirerName",
                    dataIndex: "acquirerName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "acquirerName" ? sortInfo.order : null,
                },
                {
                    title: t("hostProductCodeList.internalCode"),
                    key: "internalCode",
                    dataIndex: "internalCode",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "internalCode" ? sortInfo.order : null,
                },
                {
                    title: t("hostProductCodeList.hostProductCode"),
                    key: "hostProductCode",
                    dataIndex: "hostProductCode",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "hostProductCode" ? sortInfo.order : null,
                },
                {
                    title: t("hostProductCodeList.hostAdditionalProductCode"),
                    key: "hostAdditionalProductCode",
                    dataIndex: "hostAdditionalProductCode",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "hostAdditionalProductCode" ? sortInfo.order : null,
                },
                {
                    title: t("genericTable.column.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: HostProductCodeList) => {
        navigate(`${BASE_PATH}/${ResourceName.HOST_PRODUCT_CODES}/${FORM_ACTION.EDIT}/${record.id}`);
    };

    return (
        (datasource && (
            <GenericTable<HostProductCodeList>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="hostProductCodes_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: HostProductCodeList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
